import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { LayoutDashboard, Users, Truck, ShieldAlert, Map, Mail, Settings, BarChart, Coins, Globe, Satellite } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import { useTheme } from 'next-themes';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Divider from '@/components/ui/divider';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';

const AdminGeo = () => {
    const { theme } = useTheme();
    const [mapType, setMapType] = useState('roadmap'); // 'roadmap' or 'satellite'

    // Centered on Guinea (around Conakry)
    const initialPosition = [9.9456, -9.6966]; 
    const initialZoom = 6; // Adjusted zoom level to fit most of Guinea

    const cities = [
        { name: 'Conakry', pos: [9.53795, -13.67729], activeTransporters: 120, pendingMissions: 35 },
        { name: 'Nzérékoré', pos: [7.7550, -8.8183], activeTransporters: 45, pendingMissions: 12 },
        { name: 'Kankan', pos: [10.3854, -9.3057], activeTransporters: 60, pendingMissions: 20 },
        { name: 'Kindia', pos: [10.0579, -12.8553], activeTransporters: 75, pendingMissions: 18 },
        { name: 'Labé', pos: [11.3186, -12.2833], activeTransporters: 55, pendingMissions: 15 },
    ];

    const lightThemeRoadmapUrl = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
    const darkThemeRoadmapUrl = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
    const satelliteUrl = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";


    const getTileLayerUrl = () => {
        if (mapType === 'satellite') {
            return satelliteUrl;
        }
        return theme === 'dark' ? darkThemeRoadmapUrl : lightThemeRoadmapUrl;
    };

    return (
        <>
            <Helmet>
                <title>Analyses Géographiques - LogiConnect</title>
            </Helmet>
            <DashboardLayout sidebarLinks={adminSidebarLinks}>
                <div className="space-y-8">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Analyses Géographiques</h1>
                            <p className="text-muted-foreground mt-1">Vue d'ensemble de l'activité sur le territoire.</p>
                        </div>
                        <div className="flex space-x-2">
                            <Button
                                variant={mapType === 'roadmap' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setMapType('roadmap')}
                                className="flex items-center gap-2"
                            >
                                <Globe className="w-4 h-4" /> Carte
                            </Button>
                            <Button
                                variant={mapType === 'satellite' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setMapType('satellite')}
                                className="flex items-center gap-2"
                            >
                                <Satellite className="w-4 h-4" /> Satellite
                            </Button>
                        </div>
                    </div>

                    {/* Divider */}
                    <Divider variant="gradient" icon={Map} label="Carte Interactive" />
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                           <Card className="h-[600px] w-full bg-card">
                                <CardContent className="p-0 h-full w-full rounded-xl overflow-hidden">
                                     <MapContainer 
                                        center={initialPosition} 
                                        zoom={initialZoom} 
                                        scrollWheelZoom={true} 
                                        style={{ height: '100%', width: '100%', backgroundColor: 'transparent' }} 
                                        attributionControl={false}
                                    >
                                        <TileLayer
                                            url={getTileLayerUrl()}
                                            attribution={
                                                mapType === 'satellite' 
                                                    ? '&copy; <a href="https://developers.arcgis.com/esri-leaflet/licensing/">Esri</a>, i-cubed, USDA FSA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, swisstopo, and the GIS User Community'
                                                    : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">Carto</a>'
                                            }
                                        />
                                        {cities.map(city => (
                                            <Marker key={city.name} position={city.pos}>
                                                <Popup>
                                                    <div className="font-sans">
                                                        <p className="font-bold text-base mb-1">{city.name}</p>
                                                        <p>Transporteurs actifs: <span className="font-semibold">{city.activeTransporters}</span></p>
                                                        <p>Missions en attente: <span className="font-semibold">{city.pendingMissions}</span></p>
                                                    </div>
                                                </Popup>
                                            </Marker>
                                        ))}
                                         <CircleMarker center={[9.53795, -13.67729]} pathOptions={{ color: 'hsl(var(--primary))', fillColor: 'hsl(var(--primary))', fillOpacity: 0.3 }} radius={30}>
                                            <Popup>Zone de forte activité : Conakry</Popup>
                                        </CircleMarker>
                                    </MapContainer>
                                </CardContent>
                           </Card>
                        </div>
                        <div className="lg:col-span-1 space-y-6">
                           <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center">
                                        <Map className="w-5 h-5 mr-2 text-primary"/>
                                        Légende
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-3 text-sm">
                                        <li className="flex items-center">
                                            <div className="w-4 h-4 rounded-full bg-blue-500/80 mr-3 border border-blue-300"></div>
                                            <span>Hub principal (Ville)</span>
                                        </li>
                                        <li className="flex items-center">
                                             <div className="w-4 h-4 rounded-full bg-primary/30 mr-3 border border-primary"></div>
                                            <span>Zone de forte activité</span>
                                        </li>
                                    </ul>
                                </CardContent>
                           </Card>
                           <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center">
                                        <BarChart className="w-5 h-5 mr-2 text-primary"/>
                                        Statistiques Clés
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                     <div className="flex justify-between items-center">
                                        <span className="text-muted-foreground">Transporteurs actifs</span>
                                        <span className="font-bold text-lg">355</span>
                                     </div>
                                     <div className="flex justify-between items-center">
                                        <span className="text-muted-foreground">Missions en cours</span>
                                        <span className="font-bold text-lg">100</span>
                                     </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-muted-foreground">Villes couvertes</span>
                                        <span className="font-bold text-lg">5</span>
                                     </div>
                                </CardContent>
                           </Card>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        </>
    );
};

export default AdminGeo;
